import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Language, translations } from '@/lib/i18n';

type TranslationType = (typeof translations)[Language];

type LanguageContextType = {
  lang: Language;
  setLang: (l: Language) => void;
  t: TranslationType;
};

const validLangs: Language[] = ['en', 'uk', 'ja'];

function getInitialLang(): Language {
  const params = new URLSearchParams(window.location.search);
  const urlLang = params.get('lang') as Language;
  if (urlLang && validLangs.includes(urlLang)) return urlLang;
  return 'en';
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Language>(getInitialLang);

  const setLang = (l: Language) => {
    setLangState(l);
    const url = new URL(window.location.href);
    if (l === 'en') {
      url.searchParams.delete('lang');
    } else {
      url.searchParams.set('lang', l);
    }
    window.history.replaceState({}, '', url.toString());
  };

  const t = translations[lang];
  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}
